// inventory-destroy-truth.ts — R1: the client-truth OBJECT → RUBBLE adapter + WS subscriber.
//
// Owner (2026-09-10): "Turning into rubble should be a front end operation … we update and do the delete and backend
// causally … so the backend gets updated lazily." Owner (2026-09-29): "the front end is the system … the backend is used for lazy save."
// The pure resolver (janus-engine/inventory/destroy-object.ts) decides from the roster + catalog this page already holds — a payload
// object's WHOLE chain too (payload-chain.ts, pre-draw); the outcome is drawn here at once and ONE request
// pushes the frame to every page; options/game/actions/destruction_chain_save_inc.php proves authority and saves it behind. A rejected
// save reverts. A click this page cannot decide returns null; the card shows a named refusal before any legacy work.
import {
  applyObjectDestruction, resolveObjectDestruction,
  type ObjectDestroyCommand, type ObjectDestroyReaders,
} from '../janus-engine/inventory/destroy-object.js';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { buildAppliedFrame } from '../janus-engine/transport/applied-frame';
import { inventorySizeRatio, inventorySizeRatioEligible } from '../encounter-creation-engine/inventory-size-ratio.js';
import { actorEncounterFor, actorRowFor } from './movement-truth.js';
import { posCoord, encounterField, encounterCreatureField } from '../shared/array-types.js';
import { fetchAndCacheObject } from './object-cache.js';
import { selectInventoryMeshSet } from '../../../ft3d/engine/mesh-presentation.js';
import { rubbleSourceFromBounds, type RubbleSource } from '../../../ft3d/engine/rubble-source.js';
// R2-3 (import only): the object-damage gate a spilled child passes before its splash is answered here.
import { FALL_UNMODELLED_OWN_LAYERS, objectFallUnmodelled } from '../janus-engine/inventory/object-fall-damage.js';
import { rubbleSpawnEntry } from '../janus-engine/inventory/rubble-spawn-table.js';
import type { SplashFacts } from '../janus-engine/inventory/destroy-object.js';
// R1b-2: the payload plan (snapshot → per-step casting strings); resolving a step is Lane A's (not wired — hand-back stays).
import { planPayloadDrain, type DestructionPayloadJob, type PayloadCaster, type PayloadPlan } from '../janus-engine/inventory/destruction-payload.js';
// R1b-2 wiring (import only — Lane A A17): the environmental-payload runner, its page glue (dry run → draw → request; commit), and the
// ability readers it runs on. R1b dry-runs the WHOLE chain with these before anything commits.
import { environmentalPayloadContext, PAYLOAD_DRY_PATTERNS, payloadDryRunRollSource, runEnvironmentalPayload, type PayloadVerdict } from '../janus-engine/ability/environmental-payload.js';
import { HandBack, Refusal } from '../janus-engine/ability/context.js';
import { browserRollSource } from '../janus-engine/actions/weapon-attack.js';
import { abilityTruthReaders, fetchObjectsBatch } from './ability-truth.js';
import { certifyMembers, prefetchPayloadDefinitions } from './environmental-payload-truth.js';
// R1b-2f: the decided chain as ONE frame — drawn here, relayed to every page, saved lazily (destruction_chain_save_inc.php).
import { applyChainOpsWithCorrection, buildChainOps, DESTRUCTION_CHAIN_SCHEMA, type ChainOp, type DestructionChainFrame } from '../janus-engine/inventory/payload-chain-frame.js';
import { prepareDestructionSave } from '../janus-engine/inventory/destruction-save.js';
import { refreshSavedInventory } from '../shared/inventory-saved-refresh.js';
import type { BasePatchFrame } from '../janus-engine/compile/base-patch.js';
import { handleCasterUpdate } from './caster-update.js';
// D-049: the shared undo-on-failed-save engine (a failed lazy save reverts exactly this chain's writes, three-way)
import { classifyRefusal, RevertLedger, type OptimisticDraw } from '../shared/revert-on-refusal.js';
// R1b-2d: the whole chain (every job it causes, every caused destruction) in one pure walk, and legacy's row order as commits.
import { enqueueCaused, payloadCommitPlan, runPayloadChain, type CausedResolution, type ChainCaused, type ChainWorld, type PayloadChainResult, type ResolveStep } from '../janus-engine/inventory/payload-chain.js';
import { createInventoryDamageDefinitionWarmup } from '../janus-engine/inventory/damage-definition-warmup.js';
import { inventoryRowList, readInventoryField } from '../janus-engine/compile/inventory-raw-readers.js';
import { registerActionInputWarmup } from './action-input-warmup.js';
import { fullDefinition } from '../janus-engine/reactions/php-row-rules.js';
import { frameCopyOnRead } from '../janus-engine/ability/frame-snapshot.js';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const RETRIES = 3;
/** R1b: the branches the lazy save (destruction_chain_save_inc.php) persists for the GM tool. */
const GM_TOOL_BRANCHES = { door: true, spill: true, wall: true, payload: true, corpse: true } as const;
/** R1b-2: who the GM tool's payload is attributed to — turn_inventory_to_rubble_inc.php's `$caster_arr`: the selected creature when the
 * request names one (Lane A stage 1 hands a creature caster back), else the acting USER stub. */
function gmPayloadCaster(casterId?: string, encounter?: Row | null): PayloadCaster {
  if (!casterId || !upper(casterId)) return { kind: 'user', id: upper(window.FateTide?.user?.id) };
  // A creature caster also AIMS a cone / line payload (janus_trigger_auto_place_aoe's inciting point): its floor position when held.
  const member = (Array.isArray(encounter?.encounter_creatures_arr) ? encounter!.encounter_creatures_arr as unknown[] : [])
    .map(asRow).find(m => upper(asRow(m?.encounter_creatures)?.encounter_creature_id) === upper(casterId)) ?? null;
  const pos = asRow(member?.position) ?? asRow(member?.encounter_creatures);
  const x = Number(pos?.x_pos), y = Number(pos?.y_pos);
  return { kind: 'creature', id: upper(casterId), ...(member && Number.isFinite(x) && Number.isFinite(y) ? { position: { x_pos: x, y_pos: y } } : {}) };
}

/** Operations this browser owns: provisional echoes are already drawn; terminal echoes adopt saved inputs. */
const own = new Set<string>();
/** Frames already applied per operation (dedup across provisional → committed → correction). */
const seen = new Map<string, Set<string>>();
function noteSeen(operationId: string, kind: string): boolean {
  const kinds = seen.get(operationId) ?? new Set<string>();
  if (kinds.has(kind) || (kind === 'provisional' && (kinds.has('committed') || kinds.has('correction')))) return false;
  kinds.add(kind); seen.set(operationId, kinds);
  if (seen.size > 256) seen.delete(seen.keys().next().value!);
  return true;
}

function inventoryId(row: Row | null): string {
  return upper(asRow(row?.inventory)?.inventory_id ?? row?.inventory_id);
}
function catalogOf(encounter: Row, row?: Row | null): Row {
  return { ...(asRow(encounter.catalog) ?? {}), ...(asRow(row?.catalog) ?? {}) };
}
function definitionFor(encounter: Row, id: string): Row | null {
  const key = upper(id);
  const full = (entry: unknown): Row | null => { const def = asRow(entry); return def && asRow(def.items) ? def : null; };
  const held = full(asRow(encounter.catalog)?.[key]);
  if (held) return held;
  for (const row of inventoryRowList(encounter.encounter_inventories_arr)) {
    const found = full(asRow(row.catalog)?.[key]);
    if (found) return found;
  }
  return full(window.FateTide?.objectCache?.get(key));
}
/** The first world mesh's measured bounds (catalog `assets['3d'][].boundsM`, mesh contract v5), primary first. */
function meshBoundsFor(def: Row | null): [number, number, number] | null {
  const meshes = Array.isArray(asRow(def?.assets)?.['3d']) ? asRow(def?.assets)!['3d'] as unknown[] : [];
  const world = meshes.map(asRow).filter((m): m is Row => !!m && (m.role ?? 'world') === 'world');
  world.sort((a, b) => Number(b.primary === true) - Number(a.primary === true) || Number(a.order ?? 0) - Number(b.order ?? 0));
  const bounds = world.map(m => m.boundsM).find(b => Array.isArray(b) && b.length === 3 && b.every(n => Number.isFinite(Number(n)) && Number(n) > 0));
  return bounds ? (bounds as unknown[]).map(Number) as [number, number, number] : null;
}
function readers(encounter: Row, sourceZ: number): ObjectDestroyReaders {
  const sizeTypes = (window as unknown as { SIZE_TYPES?: unknown[] }).SIZE_TYPES;
  const read: ObjectDestroyReaders = {
    value: (target, key) => ftLayerValue({ ...target, catalog: catalogOf(encounter, target) }, key),
    entries: (target, key) => ftLayerEntries({ ...target, catalog: catalogOf(encounter, target) }, key),
    definition: id => definitionFor(encounter, id),
    // A grounded FT3D row persists z = the floor under it (feet plane); the pile lands on the floor its source stood on.
    floorAt: () => sourceZ,
    meshBoundsM: id => meshBoundsFor(definitionFor(encounter, id)),
    sizeBaseFt: id => {
      if (!Array.isArray(sizeTypes)) return null;
      const entry = sizeTypes.map(asRow).find(t => upper(t?.item_id) === upper(id));
      const n = Number(entry?.base_size);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    uuid: () => crypto.randomUUID().toUpperCase(),
    splash: child => splashFacts(encounter, child),
    // a spilled child the splash destroys was never placed: its pile fits the model it would draw (round 5 session C)
    drawnGeometry: row => drawnSourceGeometry(encounter, row, read),
    hiddenLinks: id => hiddenLinksOf(upper(id)),
    // N10 (R-021 step 2): ft_painted_wall_destroy_section()'s binding — a persistent AoE anchor on this encounter (status_aoe_source 1) whose
    // geometry's `sections.entries[*].inventoryId` names the row. A geometry this page cannot parse = unknown.
    paintedWallBound: id => {
      const list = asRow(encounter.statuses)?.target ?? asRow(asRow(encounter.encounters)?.statuses)?.target ?? [];
      let unknown = false;
      for (const st of (Array.isArray(list) ? list : Object.values(asRow(list) ?? {})).map(asRow)) {
        if (!st || Number(st.status_aoe_source ?? 0) !== 1 || st.status_aoe_geometry === null || st.status_aoe_geometry === undefined) continue;
        let doc: unknown = st.status_aoe_geometry;
        if (typeof doc === 'string') { try { doc = JSON.parse(doc); } catch { unknown = true; continue; } }
        const entries = asRow(asRow(doc)?.sections)?.entries;
        if (!Array.isArray(entries)) continue;
        if (entries.some(e => upper(asRow(e)?.inventoryId) === upper(id))) return true;
      }
      return unknown ? null : false;
    },
    corpseBody: id => {
      // Dead members already carry their possessions on the held encounter. They
      // need no active-caster cache entry or creature recompile to spill them.
      const member = inventoryRowList(encounter.encounter_creatures_arr)
        .find(row => upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id')) === upper(id));
      const holder = member && (Array.isArray(member.inventory) || asRow(member.inventory))
        ? member : asRow(window.FateTide?.casterCache?.get(upper(id)));
      if (!Array.isArray(holder?.inventory) && !asRow(holder?.inventory)) return null;
      return inventoryRowList(holder!.inventory).filter(row => upper(readInventoryField(row, 'possess_id')) === upper(id)
        && Number(readInventoryField(row, 'inventory_deleted') ?? 0) !== 1);
    },
  };
  return read;
}

/** Same held geometry, contents and corpse readers for an ability's inventory destruction. */
export function abilityDamageDestructionReaders(encounter: Row, source: Row): ObjectDestroyReaders {
  return { ...readers(encounter, Number(posCoord(source, 'z_pos') ?? 0)),
    hiddenLinks: id => abilityLinkFacts.get(upper(id)) ?? null };
}
Object.assign(window, { ftAbilityDamageDestructionReaders: abilityDamageDestructionReaders });

// Installs check inventory identities, not movement/vitality. Completed inputs and
// missing answers survive ordinary actions and unchanged encounter broadcasts.
const damageDefinitionWarmup = createInventoryDamageDefinitionWarmup({
  identity: encounter => upper(encounterField(encounter, 'encounter_id')),
  epoch: () => {
    const store = (globalThis as unknown as { __ftDefinitionStore?: { epoch?: number | null; cacheGeneration?: number } }).__ftDefinitionStore;
    return `${store?.epoch}:${store?.cacheGeneration}`;
  },
});
function prepareDamageDefinitions(encounter: Row): Promise<void> {
  return damageDefinitionWarmup.prepare(encounter, {
    definition: id => fullDefinition(asRow(window.FateTide?.objectCache?.get(upper(id))), 'any') ?? definitionFor(encounter, id),
    entries: (target, key) => ftLayerEntries(target, key) ?? [],
  }, fetchObjectsBatch);
}
registerActionInputWarmup('inventory-damage', async encounter => {
  try {
    await prepareDamageDefinitions(encounter);
    return true;
  }
  catch (error) {
    console.warn('Object damage preload failed', error);
    // A failed read leaves the affected id unknown. Let the resolver name it
    // before dice; an unrelated action does not depend on this object's rubble.
    return true;
  }
});
for (const event of ['ft:encounter-installed', 'ft:encounter-state-received']) window.addEventListener(event, e => {
  const detail = asRow((e as CustomEvent).detail);
  if (detail?.inventoryUnchanged === true) return;
  const encounter = asRow(detail?.encounterArr);
  if (encounter) {
    damageDefinitionWarmup.observe(encounter);
    void prepareDamageDefinitions(encounter).catch(error => console.warn('Object damage preload failed', error));
  }
});
// Caster repaints observe carried edits for the next action; only inventory adoption preloads now.
for (const event of ['ft:caster-state-applied', 'ft:inventory-base-compiled']) window.addEventListener(event, e => {
  const detail = asRow((e as CustomEvent).detail);
  if (detail?.inventoryUnchanged === true) return;
  const caster = asRow(detail?.casterArr) ?? asRow(window.FateTide?.casterCache?.get(upper(detail?.entityId ?? detail?.casterId)));
  const id = upper(detail?.encounterId ?? encounterCreatureField(asRow(caster?.encounter_creatures), 'encounter_id'));
  const encounter = asRow(window.FateTide?.encounterCache?.get(id));
  if (encounter && caster) {
    damageDefinitionWarmup.observeMember(encounter, caster);
    if (event === 'ft:inventory-base-compiled')
      void prepareDamageDefinitions(encounter).catch(error => console.warn('Object damage preload failed', error));
  }
});

const numOrNull = (v: unknown): number | null => v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
damageDefinitionWarmup.bindCache(() => window.FateTide?.objectCache);
/**
 * vitality_of() + ft_hydrate_vitality() on a page row (the I1 fall lane's rule, memory feedback_client_truth_lean_inventory_row_
 * hydration): the row's compiled vitality over its template's, then base max / current / temp, current clamped to
 * [0, max(1, max + max-temp)]; an already-hydrated bucket (v_status set) as it is. Null = no vitality anywhere → unknowable.
 */
function hydratedVitality(def: Row | null, row: Row): { vCurrent: number; vTemp: number; effectiveVMax: number } | null {
  const v = { ...(asRow(asRow(def?.computed)?.vitality) ?? {}), ...(asRow(asRow(row.computed)?.vitality) ?? {}) } as Row;
  if (!Object.keys(v).length) return null;
  const has = (k: string): boolean => Object.prototype.hasOwnProperty.call(v, k);
  const int = (x: unknown): number => { const n = Number(x); return Number.isFinite(n) ? Math.trunc(n) : 0; };
  if (v.v_status !== undefined && v.v_status !== null) {
    const cur = numOrNull(v.v_current), eff = numOrNull(v.effective_v_max);
    return cur === null || eff === null ? null : { vCurrent: cur, vTemp: numOrNull(v.v_temp) ?? 0, effectiveVMax: eff };
  }
  const baseMax = int(v.base_v_max ?? v.v_max ?? 0);
  const baseCur = has('base_v_current') ? int(v.base_v_current) : has('v_current') ? int(v.v_current) : baseMax;
  const temp = int(v.base_v_temp ?? v.v_temp ?? 0);
  const maxTemp = int(v.base_v_max_temp ?? v.v_max_temp ?? 0);
  const eff = Math.max(1, baseMax + maxTemp);
  const cur = has('v_current') ? numOrNull(v.v_current) : Math.max(0, Math.min(baseCur, eff));
  const effective = has('effective_v_max') ? numOrNull(v.effective_v_max) : eff;
  return cur === null || effective === null ? null : { vCurrent: cur, vTemp: has('v_temp') ? numOrNull(v.v_temp) ?? 0 : temp, effectiveVMax: effective };
}
/**
 * R1b-3b: what a spilled child's splash needs. R2's objectFallUnmodelled() decides whether the total can be answered here (its
 * status / layer gate is a superset of what the splash reads); fragile = its effective composition carries
 * fragile_container_splash (the row's own first item_composition, else the template's). Null = not establishable.
 */
function splashFacts(encounter: Row, child: Row): SplashFacts | null {
  const inv = asRow(child.inventory) ?? child;
  const templateId = uuidOf(inv.inventory_item_id);
  const def = templateId ? definitionFor(encounter, templateId) : null;
  if (!def) return null;
  const withCatalog = { ...child, catalog: catalogOf(encounter, child) };
  const value = (key: string): unknown => ftLayerValue(withCatalog, key);
  const entries = (key: string): unknown[] => ftLayerEntries(withCatalog, key);
  const bucket = child.statuses;
  const targets = asRow(bucket)?.target;
  if ((bucket !== undefined && bucket !== null && !asRow(bucket)) || (targets !== undefined && targets !== null && !Array.isArray(targets))) return null;
  const objectCache = window.FateTide?.objectCache as Map<string, unknown> | undefined;
  const statuses = (Array.isArray(targets) ? targets : []).map(asRow).filter((st): st is Row => !!st).map(st => {
    const sid = upper(asRow(st.items)?.item_id ?? st.status_item_id ?? asRow(st.status_arr)?.item_id);
    const embedded = asRow(st.status_arr);
    const held = embedded && embedded.item_layer_types !== undefined ? embedded : asRow(catalogOf(encounter, child)[sid]) ?? asRow(objectCache?.get(sid));
    return { id: sid, definition: held && (held.item_layer_types !== undefined || asRow(held.items)) ? held : null };
  });
  const ownLayerKeys = [...new Set([...Object.keys(asRow(def.item_layer_types) ?? {}), ...Object.keys(asRow(child.item_layer_types) ?? {}),
    ...FALL_UNMODELLED_OWN_LAYERS.filter(layer => child[layer] !== undefined && child[layer] !== null)])]
    .filter(layer => entries(layer).length > 0 || (value(layer) ?? null) !== null || (child[layer] ?? null) !== null);
  const hasContent = (v: unknown): boolean => Array.isArray(v) ? v.some(hasContent) : asRow(v) ? Object.values(v as Row).some(hasContent)
    : v !== null && v !== undefined && v !== '' && v !== 0 && v !== '0' && v !== false;
  let generation = inv.inventory_generation;
  if (typeof generation === 'string') { try { generation = JSON.parse(generation); } catch { generation = null; } }
  const unmodelled = objectFallUnmodelled({
    ownLayerKeys, statuses, itemType: String(asRow(def.items)?.item_type ?? ''), wallRun: !!asRow(asRow(generation)?.wallRun),
    // The GM tool's viewer is the GM (never private-to-viewer); the splash rolls nothing (no roll-type modifiers, no house rules).
    privateToViewer: false, rollTypeModifiers: hasContent(asRow(child.computed)?.roll_type_modifiers), campaignHeld: true,
  });
  const vitality = hydratedVitality(def, child);
  if (!vitality) return null;
  const composition = uuidOf(entries('item_composition')[0] ?? value('item_composition'));
  return { unmodelled, ...vitality, fragile: rubbleSpawnEntry(composition)?.fragileSplash === true };
}
const uuidOf = (value: unknown): string => {
  const raw = Array.isArray(value) ? value[0] : value;
  const id = upper(typeof raw === 'string' ? raw : asRow(asRow(raw)?.items)?.item_id ?? asRow(raw)?.item_id);
  return /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(id) ? id : '';
};
/**
 * The source's DRAWN footprint in feet (larger horizontal axis) — what the scene renders: world-mesh bounds × its
 * transform scale × the instance-size ratio (inventory-size-ratio.ts). Same rule as PHP ft_rubble_fit_transform().
 * Null without mesh bounds (the resolver then falls back to the size category, as PHP does).
 */
function drawnSourceGeometry(encounter: Row, source: Row, read: ObjectDestroyReaders): RubbleSource | null {
  const inv = asRow(source.inventory) ?? source;
  const templateId = uuidOf(inv.inventory_item_id);
  const def = templateId ? definitionFor(encounter, templateId) : null;
  // Honor the instance's selected look, as the actual renderer does. A multi-part look needs a
  // combined contact frame; do not claim the dimensions of just its first component.
  const selected = selectInventoryMeshSet({...source, catalog: catalogOf(encounter, source)}, {role:'world',bodyProfile:'any',quality:'automatic',entity:source});
  const bounds = selected?.meshes?.length === 1 ? selected.meshes[0]?.boundsM : null;
  if (!Array.isArray(bounds) || bounds.length !== 3) return null;
  let rawTransform = inv.transform_3d;
  if (typeof rawTransform === 'string') { try { rawTransform = JSON.parse(rawTransform); } catch { rawTransform = null; } }
  const transform = asRow(rawTransform);
  const v1 = transform?.schema === 'ft.inventory-transform-3d.v1';
  const scale = v1 && Number(transform?.scale) > 0 ? Number(transform?.scale) : 1;
  const itemType = String(asRow(def?.items)?.item_type ?? '');
  const ratio = inventorySizeRatioEligible({
    hasNativeMesh: true, isOverrideMesh: false, isRubbleFallback: false,
    absoluteTransform: v1 && transform?.scaleMode === 'absolute', itemType,
  }) ? inventorySizeRatio(uuidOf(inv.inventory_size), uuidOf(def ? ftLayerValue(def, 'size') : null), read.sizeBaseFt) : 1;
  const axes = Array.isArray(transform?.scaleXYZ) && transform.scaleXYZ.length === 3
    && transform.scaleXYZ.every(n=>typeof n==='number'&&Number.isFinite(n)&&n>=.05&&n<=24)
    ? transform.scaleXYZ as number[] : [1,1,1];
  return rubbleSourceFromBounds(bounds.map((n,i)=>Number(n)*scale*ratio*(axes[i]??1)));
}
/** The pile's template goes into the roster catalog so every consumer of the roster can draw the new row. */
function internDefinition(encounter: Row, itemId: unknown): void {
  const key = upper(itemId);
  const catalog = asRow(encounter.catalog);
  if (!key || !catalog || asRow(catalog[key])) return;
  const def = definitionFor(encounter, key);
  if (def) catalog[key] = def;
}

/** D-048: rows this click's chain placed → what left them (and whether that was a door: legacy migrates only a door's links onto its pile). */
const pileOrigin = new Map<string, { origin: string; door: boolean }>();
/** D-048: live links on either end of a row, as the server knows them (destruction_link_facts_inc.php), for this click. */
const linkFacts = new Map<string, boolean>();
/** Held ability inputs survive GM boundaries and are refilled by successful fact reads. */
const abilityLinkFacts = new Map<string, boolean>();
/** Links another encounter's rows hold on this row (or, for a pile this chain placed, on the door it replaced): null = not fetched yet. */
function hiddenLinksOf(id: string): boolean | null {
  for (let hop = 0, cur = id; hop < 32; hop++) {
    const placed = pileOrigin.get(cur);
    if (!placed) return linkFacts.has(cur) ? linkFacts.get(cur)! : null;
    if (!placed.door) return false; // a non-door's pile inherits no links (legacy)
    cur = placed.origin;
  }
  return null;
}
/** The row the server knows for `id`: itself, or — for a pile this chain placed — the (door) row it replaced, up the chain (Codex review). */
function persistedAncestor(id: string): string {
  let cur = id;
  for (let hop = 0; hop < 32 && pileOrigin.has(cur); hop++) cur = pileOrigin.get(cur)!.origin;
  return cur;
}
async function fetchLinkFacts(campaignId: string, encounterId: string, ids: string[], actorId?: string): Promise<boolean> {
  try {
    const response = await fetch(LINK_FACTS_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ campaign_id: campaignId, encounter_id: encounterId, ids, ...(actorId ? { actor_id: actorId } : {}) }), signal: AbortSignal.timeout(15000) });
    const json = await response.json().catch(() => null) as { ok?: boolean; facts?: Record<string, unknown> } | null;
    if (!response.ok || json?.ok !== true || !asRow(json.facts)) return false;
    for (const id of ids) { const v = json.facts![upper(id)]; if (typeof v === 'boolean') linkFacts.set(upper(id), v); else return false; }
    return true;
  } catch { return false; }
}
const LINK_FACTS_ENDPOINT = '/options/game/actions/destruction_link_facts_inc.php';
Object.assign(window, {
  ftAbilityDamageLinkFacts: async (encounter: Row, actorId: string, ids: string[]) => {
    if (!await fetchLinkFacts(upper(encounterField(encounter, 'encounter_master_parent_id')), upper(encounterField(encounter, 'encounter_id')), ids, actorId)) return false;
    for (const id of ids) abilityLinkFacts.set(upper(id), linkFacts.get(upper(id))!);
    return true;
  } });

/** Every row a destruction places (its pile, a D-048 waypoint marker — and those of the children its spill's splash destroyed): their
 * templates go into the roster catalog. */
function internPlaced(encounter: Row, command: ObjectDestroyCommand): void {
  if (command.rubble) pileOrigin.set(upper(command.rubble.inventory_id), { origin: upper(command.inventory_id), door: !!command.links });
  internDefinition(encounter, command.rubble?.inventory_item_id);
  internDefinition(encounter, command.waypoint?.inventory_item_id);
  for (const nested of command.spill?.destroyed ?? []) internPlaced(encounter, nested);
}

/** Redraw every consumer of this encounter's roster (the campaign page re-projects inventories into the FT3D view). */
function rerender(encounterId: string, encounter: Row, subType: string): void {
  window.dispatchEvent(new CustomEvent('ft:encounter-state-received', { detail: { encounterId, encounterArr: encounter, subType } }));
}
function warn(message: string): void {
  if (typeof displayWarning === 'function') displayWarning(message);
}

export type ObjectDestroyOutcome = 'handled' | 'refused' | null;
/**
 * Null = no browser plan (outside R1's boundary, or missing page data); the caller shows a named refusal.
 * 'refused' = the resolver proved the legacy refusal locally (message shown). 'handled' = converted, drawn, queued.
 */
type DestroyInput = { campaignId: string; encounterId: string; inventoryId: string; casterId?: string };
/**
 * I1 H3: an object DESTROYED BY DAMAGE (a fall) — handle_inventory_destroyed_from_damage()'s outcome, from the same resolver
 * the GM's Turn to Rubble uses: rubble (the pile lands where the object came to rest) or, for a composition that leaves
 * none, a vanish. Containers share the spill resolver, at the landing spot and with the actual breaking damage.
 * The fall lane persists it with its own commit (permission is the lane's, not a GM gesture).
 */
export function planDamageDestruction(input: { encounterId: string; inventoryId: string; campaignId: string; rest: { x: number; y: number; z: number }; breakingDamage: number }):
  { kind: 'destruction'; command: ObjectDestroyCommand } | { kind: 'handBack'; reason: string } {
  const encounterId = upper(input.encounterId);
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const roster = Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : [];
  const source = roster.find(r => inventoryId(r) === upper(input.inventoryId)) ?? null;
  if (!encounter || !source) return { kind: 'handBack', reason: 'destroy-source-unavailable' };
  const landed = { ...source, position: { x_pos: input.rest.x, y_pos: input.rest.y, z_pos: input.rest.z } };
  const view = { ...encounter, encounter_inventories_arr: roster.map(row => row === source ? landed : row) };
  const read = readers(view, input.rest.z);
  const sourceGeometry = drawnSourceGeometry(view, landed, read);
  let scope;
  try {
    scope = resolveObjectDestruction({ campaignId: input.campaignId, encounterId, inventoryId: input.inventoryId, isGM: true,
      sourceFootprintFt: sourceGeometry ? sourceGeometry.footprintM / 0.3048 : null, sourceGeometry,
      branches: { spill: true }, splashChild: true, breakingDamage: input.breakingDamage }, view, read);
  } catch (error) { return { kind: 'handBack', reason: 'destroy-' + String(error) }; }
  if (scope.supported) { internPlaced(encounter, scope.command); return { kind: 'destruction', command: scope.command }; }
  return { kind: 'handBack', reason: 'destroy-' + (scope.refused ? 'refused' : scope.reason) };
}

/** A row a creature of this encounter HOLDS (N10, R-021): the row as it would lie dropped at its holder's feet, and the drop. */
type HeldSource = { row: Row; holderId: string; fromPossessId: string; at: { x_pos: number; y_pos: number; z_pos: number } };
const possessOf = (row: Row | null | undefined): string => upper(asRow(row?.inventory)?.possess_id ?? row?.possess_id);
const liveRow = (row: Row | null): row is Row => !!row && Number(asRow(row.inventory)?.inventory_deleted ?? row.inventory_deleted ?? 0) !== 1;
/**
 * Find `id` in the loaded pack of a live creature of this encounter (its caster-cache `inventory`: the rows it holds directly, each
 * bag's contents nested under `inventory_in_container` — or a flat list; both are walked) and build the row as it lands on the floor at
 * the holder's feet — legacy ft_corpse_drop_held_body_for_destruction() (possess → the encounter, unequipped, the holder's position:
 * ft_corpse_body_world_position) — with its own contents nested the way a floor row carries them (`inventory_in_container`, what the
 * spill reads). Null = no loaded creature of this encounter holds it.
 */
function heldSource(encounter: Row, encounterId: string, id: string): HeldSource | null {
  const members = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : []).map(asRow);
  const listOf = (value: unknown): Row[] => (Array.isArray(value) ? value : asRow(value) ? Object.values(asRow(value)!) : []).map(asRow).filter(liveRow);
  for (const member of members) {
    const ec = asRow(member?.encounter_creatures);
    const holderId = upper(ec?.encounter_creature_id);
    if (!member || !holderId || Number(ec?.encounter_creature_deleted ?? 0) === 1) continue;
    const pack = asRow(window.FateTide?.casterCache?.get(holderId))?.inventory;
    if (!Array.isArray(pack)) continue;
    // every row of the pack with the row it sits in (its own possess_id, else the row it is nested under — N10 staging smoke: a bag's
    // contents ride nested, so a top-level-only search never found a bagged object)
    const rows: Row[] = [];
    const parentOf = new Map<string, string>();
    const walk = (list: Row[], parentId: string, depth: number): void => {
      if (depth > 26) return;
      for (const r of list) {
        const rid = inventoryId(r);
        if (!rid || parentOf.has(rid)) continue;
        rows.push(r);
        parentOf.set(rid, possessOf(r) || parentId);
        walk(listOf(r.inventory_in_container ?? asRow(r.inventory)?.inventory_in_container), rid, depth + 1);
      }
    };
    walk(listOf(pack), holderId, 0);
    const byId = new Map(rows.map(r => [inventoryId(r), r] as const));
    const row = byId.get(id);
    if (!row) continue;
    let up = parentOf.get(id) ?? '';
    for (let hop = 0; up !== holderId && byId.has(up) && hop < 26; hop++) up = parentOf.get(up) ?? '';
    if (up !== holderId) return null;
    // the holder's spot: its position bucket (else its encounter_creatures columns); the lean roster OMITS numeric zeroes, so a missing
    // axis of a present position is 0 (N10 staging smoke: a holder at z = 0 read as unknown)
    const place = asRow(member.position) ?? (['x_pos', 'y_pos', 'z_pos'].some(k => ec?.[k] !== undefined) ? ec : null);
    if (!place) return null;
    const axis = (k: 'x_pos' | 'y_pos' | 'z_pos'): number => { const v = posCoord(place, k); return v === undefined ? 0 : Number(v); };
    const at = { x_pos: axis('x_pos'), y_pos: axis('y_pos'), z_pos: axis('z_pos') };
    if (![at.x_pos, at.y_pos, at.z_pos].every(Number.isFinite)) return null;
    const nest = (parentId: string, depth: number): Row[] => depth > 24 ? [] : rows.filter(r => parentOf.get(inventoryId(r)) === parentId)
      .map(r => {
        const child = frameCopyOnRead(r, new Map(), { catalogs: 'share' }).encounter;
        child.inventory_in_container = nest(inventoryId(r), depth + 1); return child;
      });
    const dropped = frameCopyOnRead(row, new Map(), { catalogs: 'share' }).encounter;
    const inv = asRow(dropped.inventory) ?? (dropped.inventory = {} as Row) as Row;
    inv.possess_id = encounterId;
    inv.equipped = '';
    dropped.position = { ...at };
    dropped.computed = { ...(asRow(dropped.computed) ?? {}), possessor: { type: 'encounter' } };
    dropped.inventory_in_container = nest(id, 0);
    return { row: dropped, holderId, fromPossessId: parentOf.get(id) ?? '', at };
  }
  return null;
}

function attemptObjectDestruction(input: DestroyInput, dryRun = false): ObjectDestroyOutcome | { fetch: string[] } | { payload: ObjectDestroyCommand } {
  const encounterId = upper(input.encounterId);
  const loaded = actorEncounterFor(encounterId) as Row | null;
  if (!loaded) {
    if (dryRun) return null;
    warn('This encounter is not loaded on this page — open the encounter to turn this to rubble.');
    return 'refused';
  }
  const loadedRoster = Array.isArray(loaded.encounter_inventories_arr) ? loaded.encounter_inventories_arr as Row[] : [];
  let source = loadedRoster.find(r => inventoryId(r) === upper(input.inventoryId)) ?? null;
  // N10 (R-021): a row a creature of this encounter HOLDS falls to its holder's feet first, then goes the floor path on a view that holds it
  const held = source ? null : heldSource(loaded, encounterId, upper(input.inventoryId));
  if (!source && !held) {
    if (!dryRun) window.dispatchEvent(new CustomEvent('ft:inventory-destroy-slice-unavailable', { detail: { reason: 'not-on-floor-or-loaded-pack', inventoryId: input.inventoryId } }));
    return null;
  }
  if (held) source = held.row;
  const encounter = held ? frameCopyOnRead(loaded, new Map(), { catalogs: 'share' }).encounter : loaded;
  if (held) encounter.encounter_inventories_arr = loadedRoster.concat(held.row);
  if (!source) return null;
  const isGM = window.FateTide?.user?.isGM === true
    || (window as unknown as { ftActiveViewerIsFullGm?: () => boolean }).ftActiveViewerIsFullGm?.() === true;
  const sourceZ = Number(asRow(source.position)?.z_pos ?? 0) || 0;
  const read = readers(encounter, sourceZ);
  const sourceGeometry = drawnSourceGeometry(encounter, source, read);
  // R1b-2c: the creature the GM tool acts AS (legacy's `$caster_arr` from the posted caster_id). N10 (worker pick, owner to review): a selected
  // creature this encounter's roster does not hold acts nowhere here (owner: no actor acts outside an encounter) — the GM user is the actor,
  // instead of handing the click to the legacy endpoint.
  const casterId = input.casterId && upper(input.casterId)
    && (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : [])
      .some(m => upper(asRow(asRow(m)?.encounter_creatures)?.encounter_creature_id) === upper(input.casterId)) ? upper(input.casterId) : '';
  let scope;
  try {
    scope = resolveObjectDestruction(
      { campaignId: input.campaignId, encounterId, inventoryId: input.inventoryId, isGM,
        sourceFootprintFt: sourceGeometry ? sourceGeometry.footprintM / 0.3048 : null, sourceGeometry, branches: GM_TOOL_BRANCHES,
        payloadCaster: gmPayloadCaster(casterId, encounter) },
      encounter, read,
    );
  } catch (error) {
    if (!dryRun) window.dispatchEvent(new CustomEvent('ft:inventory-destroy-slice-unavailable', { detail: { reason: String(error) } }));
    return null;
  }
  if (!scope.supported) {
    if (!scope.refused && scope.corpseCreatureIds?.length) return { fetch: scope.corpseCreatureIds.map(id => `corpse:${upper(id)}`) };
    if (!scope.refused && (scope.anchorItemId || scope.definitionIds?.length)) return { fetch: scope.anchorItemId ? [scope.anchorItemId] : scope.definitionIds! };
    if (dryRun) return null;
    if (scope.refused) { warn(scope.message); return 'refused'; }
    window.dispatchEvent(new CustomEvent('ft:inventory-destroy-slice-unavailable', { detail: { reason: scope.reason, inventoryId: input.inventoryId } }));
    return null;
  }
  if (dryRun) return null;
  const command = scope.command;
  if (held) {
    // a held payload object's chain re-reads the page's own roster (destroyWithPayload) — not modelled with a drop: the legacy lane (step 3)
    if (enqueueCaused(command, []).length) {
      window.dispatchEvent(new CustomEvent('ft:inventory-destroy-slice-unavailable', { detail: { reason: 'held-payload', inventoryId: input.inventoryId } }));
      return null;
    }
    command.held = { holder_id: held.holderId, from_possess_id: held.fromPossessId, ...held.at };
  }
  // R1b-2c: every destruction row is attributed to the acting creature (a roster member, above) — and every child its spill's splash
  // destroyed (legacy passes the same `$caster_arr` down — autopilot W5)
  if (casterId) {
    const attribute = (c: ObjectDestroyCommand): void => { c.caster_id = casterId; for (const n of c.spill?.destroyed ?? []) attribute(n); };
    attribute(command);
  }
  // R1b-2: a payload object goes through the async chain dry run (destroyWithPayload) before anything is drawn locally or sent.
  // (a child its spill's splash destroyed may carry one too — its job queues ahead of the root's, legacy order)
  if (enqueueCaused(command, []).length) return { payload: command };
  // R1b-2g (owner 2026-09-29: "the front end is the system, the backend is a lazy save"): a plain destruction is a one-op chain — drawn here,
  // relayed to every page and saved behind by ONE request (destruction_chain_save_inc.php; authority first, a rejected save reverts).
  return drawAndSave(command, [{ op: 'destroy', command }], null);
}

/**
 * The resolver's decision for one object on THIS page's current state, WITHOUT applying or sending anything — the golden
 * differential reads it immediately before the legacy endpoint runs on the same rows (R1 S1 / R1b per-branch goldens).
 */
export function planObjectDestruction(input: { campaignId: string; encounterId: string; inventoryId: string }): unknown {
  const encounterId = upper(input.encounterId);
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const roster = Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : [];
  const source = roster.find(r => inventoryId(r) === upper(input.inventoryId)) ?? null;
  if (!encounter || !source) return { supported: false, refused: false, reason: 'destroy-source-unavailable' };
  const isGM = window.FateTide?.user?.isGM === true
    || (window as unknown as { ftActiveViewerIsFullGm?: () => boolean }).ftActiveViewerIsFullGm?.() === true;
  const read = readers(encounter, Number(asRow(source.position)?.z_pos ?? 0) || 0);
  const sourceGeometry = drawnSourceGeometry(encounter, source, read);
  return JSON.parse(JSON.stringify(resolveObjectDestruction({ campaignId: input.campaignId, encounterId, inventoryId: input.inventoryId, isGM,
    sourceFootprintFt: sourceGeometry ? sourceGeometry.footprintM / 0.3048 : null, sourceGeometry, branches: GM_TOOL_BRANCHES,
    payloadCaster: gmPayloadCaster() }, encounter, read)));
}

/** janus_trigger_auto_place_aoe()'s centred footprints: the only anchor the payload commit accepts (origin = distance point). */
const CENTRED_PAYLOAD_SHAPES = new Set(['sphere', 'circle', 'cube', 'square', 'cylinder']);
const payloadUnavailable = (reason: string, inventoryId: string): null => {
  window.dispatchEvent(new CustomEvent('ft:inventory-destroy-slice-unavailable', { detail: { reason, inventoryId } }));
  return null;
};
const casterOf = (job: DestructionPayloadJob): { kind: 'user'; id: string } | { kind: 'creature'; id: string } =>
  job.attributed_caster.kind === 'user' ? { kind: 'user', id: job.attributed_caster.id } : { kind: 'creature', id: job.attributed_caster.id };

/**
 * Every job's static gates, before any draw: an anchor the payload commit cannot take (a cone / line — nothing in the catalog is one), and
 * surface chemistry inside ONE job (a damaging burst whose types light a surface the same job lays: ft_try_ignite_surfaces_at, session 1's
 * ask). The runner itself hands ignition back against any zone already on the context (an earlier job's, or the encounter's).
 */
function payloadJobGate(encounter: Row, steps: ResolveStep[]): string | null {
  if (steps.some(step => !CENTRED_PAYLOAD_SHAPES.has(step.aoe_shape))) return 'payload-directional-anchor';
  for (const step of steps) {
    if (!step.ignite) continue;
    const lights = steps.some(other => other.kind === 'surface' && ftLayerEntries(asRow(definitionFor(encounter, other.ability_id)) ?? {}, 'destroyed_by_damage_type')
      .map(entry => upper(typeof entry === 'string' ? entry.split('|')[0] : asRow(asRow(entry)?.items)?.item_id ?? asRow(entry)?.item_id))
      .some(id => step.ignite!.damage_types.includes(id)));
    if (lights) return 'payload-surface-ignition';
  }
  return null;
}

/**
 * R1b-2b / 2d: the destruction a payload step CAUSED — `id`, brought to 0 V by the step's damage (legacy handle_inventory_destroyed_from_
 * damage() inside the payload's janus()) — resolved on the WORKING roster (`encounter`: the root destruction and every earlier caused frame
 * applied). A payload-carrying object converts: its own job runs under the root's roll group, attributed to the same caster, gated by the
 * damage types that broke it (`damageTypeIds` = ft_damage_types_in_roll). A door migrates its links, a container spills (R1b-2e); a wall
 * hands back; a pile or a composition that leaves nothing vanishes. `fetch` = definitions to get first.
 */
function resolveCausedDestruction(encounter: Row, root: ObjectDestroyCommand, job: DestructionPayloadJob, abilityId: string, id: string,
  damageTypeIds: string[], breakingDamage: number): CausedResolution {
  const roster = Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : [];
  const row = roster.find(r => inventoryId(r) === upper(id)) ?? null;
  if (!row) return { reason: 'caused-source-unavailable' };
  const read = readers(encounter, Number(asRow(row.position)?.z_pos ?? 0) || 0);
  const live = actorEncounterFor(upper(root.encounter_id)) as Row | null;
  const liveRow = (Array.isArray(live?.encounter_inventories_arr) ? live!.encounter_inventories_arr as Row[] : []).find(r => inventoryId(r) === upper(id)) ?? null;
  const sourceGeometry = live && liveRow ? drawnSourceGeometry(live, liveRow, read) : null;
  const scope = resolveObjectDestruction({ campaignId: root.campaign_id, encounterId: root.encounter_id, inventoryId: id, isGM: true,
    sourceFootprintFt: sourceGeometry ? sourceGeometry.footprintM / 0.3048 : null, sourceGeometry,
    // R1b-2e: a caused door migrates its links, a caused container spills (its splash scaled by the damage that broke it — legacy
    // `$breaking_damage`); a wall still hands back (no wall-run breach inside a chain).
    branches: { door: true, spill: true, payload: true }, breakingDamage: Math.max(0, Math.trunc(breakingDamage)),
    payloadCaster: job.attributed_caster, destroyedByDamageTypes: damageTypeIds,
    cause: { kind: 'payload', roll_id: job.roll_id, source_inventory_id: job.source_inventory_id, ability_id: abilityId } }, encounter, read);
  if (scope.supported) {
    // the pile joins the working roster: a later step / job can collect it (its template must be held to read its vitality)
    internPlaced(encounter, scope.command);
    // Legacy destroys it inside the payload's janus(), whose `$caster_arr` is the payload's attributed caster: its rows name that creature
    // — and so do the rows of every child its spill's splash destroys (round 5 session C: the same `$caster_arr` all the way down).
    const attribute = (command: ObjectDestroyCommand): ObjectDestroyCommand => !root.caster_id ? command : { ...command, caster_id: root.caster_id,
      ...(command.spill?.destroyed?.length ? { spill: { ...command.spill, destroyed: command.spill.destroyed.map(attribute) } } : {}) };
    return attribute(scope.command);
  }
  if (!scope.refused && scope.linkFactIds?.length) return { fetch: scope.linkFactIds.map(id => `links:${persistedAncestor(upper(id))}`) };
  if (!scope.refused && (scope.anchorItemId || scope.definitionIds?.length)) return { fetch: scope.anchorItemId ? [scope.anchorItemId] : scope.definitionIds! };
  return { reason: scope.refused ? 'caused-refused' : scope.reason };
}

type Certified = NonNullable<Awaited<ReturnType<typeof certifyMembers>>>;
/**
 * The WHOLE chain (payload-chain.ts runPayloadChain: every job FIFO, each step on ONE context so a later job sees an earlier one's zones,
 * statuses and damage, every caused destruction applied to the working roster at once) on a CLONE of the certified encounter with the root
 * destruction applied. Throws HandBack / Refusal from the runner.
 */
function runChainOn(certified: Certified, command: ObjectDestroyCommand, placementId: string, roll: Parameters<typeof environmentalPayloadContext>[3],
  world: ChainWorld = 'natural', only?: { jobs: DestructionPayloadJob[]; extra: Row[] }, forcedTypes?: (inventoryId: string) => string[]): PayloadChainResult<PayloadVerdict> {
  const clone = frameCopyOnRead(certified.encounter, certified.members, { catalogs: 'share' }).encounter;
  applyObjectDestruction(clone, command.frame);
  internPlaced(clone, command);
  // D-048 `isolated`: every row any world could put on the floor (piles, markers, released children) joins the roster
  if (only?.extra.length) {
    const roster = clone.encounter_inventories_arr as Row[];
    for (const row of only.extra) if (!roster.some(r => inventoryId(r) === inventoryId(row))) {
      roster.push(frameCopyOnRead(row, new Map(), { catalogs: 'share' }).encounter);
      internDefinition(clone, asRow(row.inventory)?.inventory_item_id);
    }
  }
  const roots = only?.jobs ?? enqueueCaused(command, []);
  const root = roots[0]!;
  const members = new Map<string, Row>();
  for (const member of Array.isArray(clone.encounter_creatures_arr) ? clone.encounter_creatures_arr : []) {
    const id = upper(asRow(asRow(member)?.encounter_creatures)?.encounter_creature_id);
    if (id) members.set(id, member as Row);
  }
  // Lane A's casterRowFor: a creature caster is its member row; a user is the GM tool's stub.
  const casterRow: Row = root.attributed_caster.kind === 'creature' ? members.get(root.attributed_caster.id) ?? {}
    : { encounter_creatures: { encounter_creature_id: root.attributed_caster.id, encounter_id: root.encounter_id }, items: { item_type: 'user' } };
  const read = abilityTruthReaders(casterRow, clone, certified.complete, { targetId: placementId });
  const ctx = environmentalPayloadContext(clone, members, read, roll);
  return runPayloadChain<PayloadVerdict>(roots, {
    read: readers(clone, 0),
    gate: (_job, steps) => payloadJobGate(clone, steps),
    runStep: (job, step) => runEnvironmentalPayload(ctx as never, { abilityId: step.ability_id, castingTargets: step.casting_targets, caster: casterOf(job),
      rollId: job.roll_id, excludeInventoryId: job.source_inventory_id }),
    // D-048 (Codex review): in the forced world an object can break on ANY hit a draw could give it — its job is snapshotted with every damage
    // type the chain can deal (forcedTypes), so a `requires_destruction_by_damage_type` step a later hit would unlock is dry-run too
    resolveCaused: (job, step, entry) => resolveCausedDestruction(clone, command, job, step.ability_id, entry.inventory_id,
      world === 'forced' && forcedTypes ? [...new Set([...entry.damageTypeIds.map(upper), ...forcedTypes(upper(entry.inventory_id))])] : entry.damageTypeIds, entry.total),
    apply: frame => { applyObjectDestruction(clone, frame); applyObjectDestruction(ctx.encounter, frame); },
  }, world);
}

/** D-048: the forced and isolated worlds decide the chain's SHAPE and every target a job can meet; the extremes decide damage and contests
 * there (all max = the most damage and destruction, all min, `hostile` = every contest lost at the most damage). The natural world keeps
 * the full dry-pattern set. */
const SHAPE_PATTERNS = ['max', 'min', 'hostile'] as const;
/** The damage types a payload ability deals (its own `damage_type` entries), upper-case. */
function jobDamageTypes(encounter: Row, abilityId: string): string[] {
  const def = definitionFor(encounter, abilityId);
  return def ? ftLayerEntries(def, 'damage_type').map(entry => upper(typeof entry === 'string' ? entry.split('|')[0] : asRow(asRow(entry)?.items)?.item_id ?? asRow(entry)?.item_id))
    .filter(id => /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(id)) : [];
}
/** A job's identity across worlds: its source row, where it went off (a spilled child's slot differs from its floor spot) and what broke it
 * (a `requires_destruction_by_damage_type` step runs or skips on that). */
const jobKey = (job: DestructionPayloadJob): string => [upper(job.source_inventory_id), job.ability_ids.map(upper).join(','),
  [...job.destroyed_by_damage_types].map(upper).sort().join(','), job.origin.x_pos, job.origin.y_pos, job.origin.z_pos].join('|');
/** Every row a world's caused destructions put on the floor — a pile / waypoint marker named by the row that left it (its id is fresh per
 * run), a released child by its own id — the first world's copy of each. */
function placedRows(run: { caused: ChainCaused[] }, into: Map<string, Row>): void {
  const walk = (command: ObjectDestroyCommand): void => {
    for (const nested of command.spill?.destroyed ?? []) walk(nested);
    for (const row of (command.frame.released ?? []) as Row[]) { const id = inventoryId(row); if (id && !into.has(id)) into.set(id, row); }
    const own = upper(command.rubble?.inventory_id ?? command.waypoint?.inventory_id);
    const placed = (command.frame.add as unknown as Row[]).find(row => inventoryId(row) === own);
    const key = `placed-by:${upper(command.inventory_id)}`;
    if (placed && !into.has(key)) into.set(key, placed);
  };
  for (const c of run.caused) walk(c.command);
}

/**
 * D-048 (owner 2026-09-30: "resolve every chained-destruction shape in the browser; no PHP fallback"): every shape a draw could reach is
 * decided BEFORE the draw — nothing on the page touched. Three worlds (payload-chain.ts ChainWorld) on every dry-dice pattern
 * (PAYLOAD_DRY_PATTERNS): `natural`, `forced` (every object a damaging step hit is destroyed there — the closure of every caused destruction
 * and every job), then each job the closure reached ALONE on a roster holding every row any world placed (`isolated`). Any hand-back, a
 * refusal or a runner fault in any world → the click is handed back pre-draw. The real draw then walks `natural` over shapes already
 * resolved; if it still fails that is a defect (`postDraw`): refused on the page, never handed to the legacy tool (no re-roll there).
 * `fetch` = definitions a caused job / pile needs (the caller fetches and runs it all again).
 */
async function resolvePayloadChain(encounterId: string, command: ObjectDestroyCommand, placementId: string):
  Promise<{ chain: Extract<PayloadChainResult<PayloadVerdict>, { ok: true }> } | { reason: string; fetch?: string[]; postDraw?: true }> {
  markChain('chainEnter');
  const certified = await certifyMembers(encounterId);
  markChain('certified');
  if (!certified) return { reason: 'payload:environmental-payload:member-compile-not-held' };
  const thrown = (error: unknown): { reason: string } => error instanceof HandBack ? { reason: `payload:${error.reason}` }
    : error instanceof Refusal ? { reason: `payload:refusal:${error.refusal}` } : { reason: `payload-dry-run:${String((error as Error)?.message ?? error).slice(0, 80)}` };
  const jobs = new Map<string, DestructionPayloadJob>();
  const placed = new Map<string, Row>();
  const rootKeys = new Set(enqueueCaused(command, []).map(jobKey));
  // per job source: the damage types it deals. An object can be broken by any of them EXCEPT its own job's (that runs only after it is gone)
  let dealt = new Map<string, string[]>();
  const forcedTypes = (inventoryId: string): string[] => [...new Set([...dealt].filter(([source]) => source !== inventoryId).flatMap(([, types]) => types))];
  const dry = (world: ChainWorld, only?: { jobs: DestructionPayloadJob[]; extra: Row[] }): { reason: string; fetch?: string[] } | null => {
    for (const pattern of world === 'natural' ? PAYLOAD_DRY_PATTERNS : SHAPE_PATTERNS) {
      let run: PayloadChainResult<PayloadVerdict>;
      try { run = runChainOn(certified, command, placementId, payloadDryRunRollSource(pattern), world, only, forcedTypes); } catch (error) { return thrown(error); }
      // every chain hand-back is named under `payload:` (R1b's smoke / golden replay read that prefix)
      if (!run.ok) return { reason: `payload:${run.reason}`, ...(run.fetch ? { fetch: run.fetch } : {}) };
      for (const r of run.jobs) if (!jobs.has(jobKey(r.job))) jobs.set(jobKey(r.job), r.job);
      placedRows(run, placed);
    }
    return null;
  };
  const natural = dry('natural');
  if (natural) return natural;
  // the forced world to a fixed point over the breaking damage types: every type any job the closure reaches can deal
  const live = actorEncounterFor(upper(command.encounter_id)) as Row | null;
  const typesOf = (): Map<string, string[]> => {
    const out = new Map<string, string[]>();
    for (const job of jobs.values()) {
      const source = upper(job.source_inventory_id);
      out.set(source, [...new Set([...(out.get(source) ?? []), ...job.ability_ids.flatMap(id => live ? jobDamageTypes(live, id) : [])])].sort());
    }
    return out;
  };
  dealt = typesOf();
  for (let pass = 0; pass < 6; pass++) {
    const forced = dry('forced');
    if (forced) return forced;
    const next = typesOf();
    if ([...next].every(([source, types]) => types.every(t => dealt.get(source)?.includes(t)))) break;
    dealt = next;
  }
  for (const [key, job] of [...jobs]) {
    if (rootKeys.has(key)) continue; // a root job always runs on the roster the other two worlds already walked
    const alone = dry('isolated', { jobs: [job], extra: [...placed.values()] });
    if (alone) return alone;
  }
  markChain('dryRuns');
  (window as unknown as { __ftDestroyChainWorlds?: unknown }).__ftDestroyChainWorlds = { jobs: jobs.size, placed: placed.size, patterns: PAYLOAD_DRY_PATTERNS.length,
    shapePatterns: SHAPE_PATTERNS.length, runs: PAYLOAD_DRY_PATTERNS.length + SHAPE_PATTERNS.length * (1 + [...jobs.keys()].filter(k => !rootKeys.has(k)).length) };
  let real: PayloadChainResult<PayloadVerdict>;
  try { real = runChainOn(certified, command, placementId, browserRollSource()); } catch (error) { return { ...thrown(error), postDraw: true }; }
  if (!real.ok) return { reason: `payload:${real.reason}`, postDraw: true };
  return { chain: real };
}

/**
 * R1b-2 / 2b / 2d: a PAYLOAD object's Turn to Rubble — legacy handle_inventory_destroyed_from_damage() + ft_drain_environmental_payloads().
 * Every decision is taken before anything is drawn on the page or sent: the root plan (definitions fetched) and its static gates, then the
 * WHOLE chain — every job it causes, every destruction any step causes (each must resolve here) — on four dice patterns, then the real draw
 * (resolvePayloadChain). Only then the whole outcome is drawn here and saved by ONE lazy request (drawAndSave), its ops in legacy's row
 * order (payload-chain-frame.ts buildChainOps).
 */
/** The last payload click's phase times (ms since the click), for the smoke's receipt and a console read — never gameplay. */
const chainTiming: Record<string, number> = {};
const markChain = (phase: string): void => { chainTiming[phase] = Math.round(performance.now() - (chainTiming.t0 ?? performance.now())); };
async function destroyWithPayload(command: ObjectDestroyCommand): Promise<ObjectDestroyOutcome> {
  for (const key of Object.keys(chainTiming)) delete chainTiming[key];
  pileOrigin.clear(); linkFacts.clear();
  chainTiming.t0 = performance.now();
  (window as unknown as { __ftDestroyChainTiming?: unknown }).__ftDestroyChainTiming = chainTiming;
  const encounterId = upper(command.encounter_id);
  // the root's job and those of the children its spill's splash destroyed, in legacy's queue order (autopilot W5)
  const roots = enqueueCaused(command, []);
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const roster = Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : [];
  const source = roster.find(r => inventoryId(r) === upper(command.inventory_id)) ?? null;
  if (!encounter || !source) return null;
  // The payload commit requires the source DELETED under the roll group: a breached run keeps its row.
  if (command.payload && command.outcome !== 'rubble') return payloadUnavailable('payload-on-breach', command.inventory_id);
  const read = readers(encounter, Number(asRow(source.position)?.z_pos ?? 0) || 0);
  const steps: ResolveStep[] = [];
  for (const job of roots) {
    let plan = planPayloadDrain(read, job);
    if ('reason' in plan && plan.definitionIds?.length) {
      const fetched = await Promise.all(plan.definitionIds.map(fetchAnchor));
      if (fetched.some(def => !def)) return payloadUnavailable('payload-definition-missing', command.inventory_id);
      plan = planPayloadDrain(read, job);
    }
    if ('reason' in plan) return payloadUnavailable(plan.reason, command.inventory_id);
    const jobSteps = plan.steps.filter((step): step is ResolveStep => step.action === 'resolve');
    // The roots' static gates name the case before any definition fetch or dry run (every caused job meets them again inside the chain).
    const gated = payloadJobGate(encounter, jobSteps);
    if (gated) return payloadUnavailable(gated, command.inventory_id);
    steps.push(...jobSteps);
  }
  markChain('planned');
  internDefinition(encounter, command.rubble?.inventory_item_id);

  let resolved: { chain: Extract<PayloadChainResult<PayloadVerdict>, { ok: true }> } | null = null;
  if (steps.length > 0) {
    // every payload the chain could reach (a caused object's own job included) — never a mid-chain fetch pass
    await prefetchPayloadDefinitions(encounter, [...new Set([...steps.map(step => step.ability_id), ...rosterPayloadIds(encounter)])]);
    markChain('prefetched');
    const asked = new Set<string>();
    for (let pass = 0; pass < 8 && !resolved; pass++) {
      const outcome = await resolvePayloadChain(encounterId, command, steps[0]!.casting_targets);
      if ('chain' in outcome) { resolved = outcome; break; }
      // D-048: the real draw met a shape the pre-draw worlds did not — a defect, never a legacy re-roll: nothing is drawn or saved
      if (outcome.postDraw) {
        console.error('[destruction-chain] post-draw defect: the real draw reached a shape the pre-draw worlds did not', outcome.reason);
        window.dispatchEvent(new CustomEvent('ft:inventory-destroy-chain-defect', { detail: { reason: outcome.reason, inventoryId: command.inventory_id } }));
        warn('This destruction could not be resolved (a chain the page did not foresee). Nothing was changed.');
        return 'refused';
      }
      const fresh = (outcome.fetch ?? []).map(upper).filter(id => !asked.has(id));
      if (fresh.length === 0) return payloadUnavailable(outcome.reason, command.inventory_id);
      fresh.forEach(id => asked.add(id));
      // D-048: link facts (another encounter's links on a pile the chain can destroy) come from their own read; definitions from the object API
      const linkIds = fresh.filter(id => id.startsWith('LINKS:')).map(id => id.slice('LINKS:'.length));
      const defIds = fresh.filter(id => !id.startsWith('LINKS:'));
      if (linkIds.length && !(await fetchLinkFacts(command.campaign_id, encounterId, linkIds))) return payloadUnavailable('payload-chain-link-facts', command.inventory_id);
      const fetched = await Promise.all(defIds.map(fetchAnchor));
      if (fetched.some(def => !def)) return payloadUnavailable(outcome.reason, command.inventory_id);
      // a caused job's own payload abilities: everything they name (statuses, damage types, the roster's templates) before the next pass
      if (outcome.reason === 'payload:payload-definition-missing') await prefetchPayloadDefinitions(encounter, defIds);
    }
    if (!resolved) return payloadUnavailable('payload-chain-fetch-budget', command.inventory_id);
  }

  // R1b-2f (owner 2026-09-29: "the front end is the system, the backend is a lazy save"): the WHOLE decided chain — the root swap, every
  // caused swap, the payload's zones / statuses / vitality — is drawn here at once, relayed to every other page by ONE save request, and
  // saved behind it in legacy's row order (destruction_chain_save_inc.php). A rejected save (permission — the page validated everything
  // else before it drew) reverts this page; a failed save after the relay pushes the saved encounter to every page.
  const chain = resolved?.chain ?? null;
  const ops: ChainOp[] = chain
    ? buildChainOps(command, chain as unknown as Parameters<typeof buildChainOps>[1], payloadCommitPlan(chain), () => crypto.randomUUID().toUpperCase())
    : [{ op: 'destroy', command }];
  markChain('resolved');
  const handled = drawAndSave(command, ops, { jobs: chain?.jobs.length ?? 0, steps: chain ? chain.jobs.reduce((n, run) => n + run.steps.length, 0) : 0,
    ops: ops.length, caused: ops.filter(op => op.op === 'destroy').length - 1 });
  markChain('drawn');
  return handled ?? payloadUnavailable('payload-chain-local-apply', command.inventory_id);
}

/**
 * R1b-2f / 2g: draw a decided chain on this page (every copy of the encounter + the creatures it touches), relay it to every page and save it
 * behind in ONE request. A rejected / failed save reverts this page (the saved encounter is pushed to the others). `payload` (a payload chain's
 * summary) also fires the payload events; null = a plain destruction. Null return = this page does not hold the encounter.
 */
function drawAndSave(command: ObjectDestroyCommand, ops: ChainOp[], payload: Row | null): 'handled' | 'refused' | null {
  const encounterId = upper(command.encounter_id);
  const encounter = actorEncounterFor(encounterId) as Row | null;
  if (!encounter) return null;
  for (const op of ops) if (op.op === 'destroy') prepareDestructionSave(op.command, id => definitionFor(encounter, id));
  const frame: DestructionChainFrame = { schema: DESTRUCTION_CHAIN_SCHEMA, operation_id: command.operation_id, campaign_id: command.campaign_id,
    encounter_id: encounterId, ops };
  try {
    const planner = (window as Window & { ftPlanDestructionRemoval?: (id: string, commands: ObjectDestroyCommand[]) => NonNullable<DestructionChainFrame['removal']> }).ftPlanDestructionRemoval;
    if (!planner) throw new HandBack('recursive-delete:browser-planner-not-loaded');
    frame.removal = planner(encounterId, ops.flatMap(op => op.op === 'destroy' ? [op.command] : []));
  } catch (error) {
    warn(error instanceof HandBack ? error.reason : `recursive-delete:planning-unavailable:${String(error)}`);
    return 'refused';
  }
  const draw = applyChainLocally(encounterId, frame);
  if (!draw) return null;
  own.add(command.operation_id);
  if (own.size > 256) own.delete(own.values().next().value!);
  chainDraws.track(command.operation_id, draw);
  const retiring = new Set(ops.flatMap(op => op.op === 'destroy' && op.command.corpse ? [upper(op.command.corpse.encounter_creature_id)] : []));
  const candidates = [command.caster_id, command.payload?.attributed_caster.kind === 'creature' ? command.payload.attributed_caster.id : '',
    window.ftGetActiveCasterId?.(), ...(Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [])
      .map(row => encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'))];
  const actor_id = candidates.map(upper).find(id => id && !retiring.has(id)
    && Number(frame.removal?.ec[id]?.encounter_creature_deleted ?? 0) !== 1 && actorRowFor(encounterId, id));
  // This lane already owns an ordered native delta applier. The applied envelope
  // carries transport identity; peers install the same ops below, once.
  if (actor_id) window.ftPushAppliedTruth?.({ type: 'janus_inventory_destroy', command: { ...frame, actor_id, provisional: true },
    applied_frame: buildAppliedFrame({ operation_id: frame.operation_id, encounter_id: encounterId, actor_id,
      rev: 0, member_revs: {}, rows: [], computed: {}, new_entities: [], deletes: [] }) });
  window.dispatchEvent(new CustomEvent('ft:inventory-destroy-truth-applied', { detail: { command } }));
  void (async () => {
    const saved = await saveChain(frame);
    // The answer accepts the lazy save. A correction that
    // already arrived wins (Codex S4 2g #5).
    if (saved.ok === true && chainDraws.settled(command.operation_id) === 'reverted') return;
    if (saved.ok === true) {
      chainDraws.accept(command.operation_id);
      window.dispatchEvent(new CustomEvent('ft:inventory-destroy-truth-committed', { detail: { command, result: saved } }));
      if (payload) window.dispatchEvent(new CustomEvent('ft:inventory-destroy-payload-committed', { detail: { command, ...payload } }));
      return;
    }
    // R-121: the refusal (403 permission / 4xx / a save that never answered) reverts this page's draw three-way
    chainDraws.refuse(command.operation_id, classifyRefusal({ status: Number(saved.status ?? 0), body: saved })
      ?? classifyRefusal({ status: 500, body: { error: 'unconfirmed' } })!);
    warn(`Turn to rubble could not be saved (${String(saved.error ?? 'error')}). The encounter was restored.`);
    window.dispatchEvent(new CustomEvent('ft:inventory-destroy-truth-refused', { detail: { command, message: saved.error ?? null } }));
    if (payload) window.dispatchEvent(new CustomEvent('ft:inventory-destroy-payload-failed', { detail: { command, saved } }));
  })();
  return 'handled';
}

const CHAIN_ENDPOINT = '/options/game/actions/destruction_chain_save_inc.php';
/** R-121: this page's optimistic chain draws — its own and the peers' relayed ones — settled once (shared/revert-on-refusal.ts). */
const chainDraws = new RevertLedger();
type ChainDraw = Pick<OptimisticDraw, 'accept' | 'refuse'>;
/** The lazy save: the same body again on a network / server failure (idempotent on the operation id); a 4xx is final. */
async function saveChain(frame: DestructionChainFrame): Promise<Row> {
  let last: Row = { ok: false, error: 'not sent' };
  for (let attempt = 1; attempt <= RETRIES; attempt++) {
    try {
      const response = await saveAppliedTruth(CHAIN_ENDPOINT, frame, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(frame), signal: AbortSignal.timeout(20000) });
      last = { status: response.status, ...((await response.json().catch(() => null) as Row | null) ?? { ok: false, error: 'unreadable response' }) };
    } catch (error) { last = { ok: false, error: String(error) }; }
    if (last.ok === true) {
      if (last.replay === true) handleDestructionChain({ ...last, encounter_id: frame.encounter_id, committed: true });
      return last;
    }
    const status = Number(last.status ?? 0);
    if (status >= 400 && status < 500) return last;
    if (attempt < RETRIES) await new Promise(r => setTimeout(r, 400 * attempt));
  }
  return last;
}

/** Every copy of this encounter the page draws from: the roster cache and, when it is a separate object, the FT3D projection's copy. */
function encounterCopies(encounterId: string): Row[] {
  const encounter = actorEncounterFor(encounterId) as Row | null;
  if (!encounter) return [];
  const ft3d = asRow((window as unknown as { ft3dEncounterArrCache?: { encounterId?: string; arr?: unknown } }).ft3dEncounterArrCache);
  const ft3dArr = ft3d && upper(ft3d.encounterId) === upper(encounterId) ? asRow(ft3d.arr) : null;
  return ft3dArr && ft3dArr !== encounter ? [encounter, ft3dArr] : [encounter];
}
/** The creatures a chain touches (a status on / by them, a vitality write) — their hydrated caster-cache rows are painted too. */
function chainCreatures(encounterId: string, ops: readonly ChainOp[]): string[] {
  const ids = new Set<string>();
  for (const op of ops) if (op.op === 'step') {
    for (const id of Object.keys(op.creatures)) ids.add(upper(id));
    for (const s of op.statuses) { ids.add(upper(s.target)); ids.add(upper(s.caster)); }
  }
  return [...ids].filter(id => id !== upper(encounterId) && !!actorRowFor(encounterId, id));
}
/**
 * Draw a chain on this page (every copy of the encounter + the touched creatures' rows) and re-project. Returns the optimistic draw (its
 * refusal puts the saved roster, statuses and creature rows back, three-way), or null when the page does not hold the encounter.
 */
function applyChainLocally(encounterId: string, frame: DestructionChainFrame): ChainDraw | null {
  const copies = encounterCopies(encounterId);
  if (!copies.length) return null;
  const creatures = chainCreatures(encounterId, frame.ops).map(id => actorRowFor(encounterId, id) as Row).filter(Boolean);
  const definition = (id: string): Row | null => definitionFor(copies[0]!, id);
  // every pile's / waypoint's template, a spill's splash-destroyed children's included (round 5 session C, D-048)
  for (const op of frame.ops) if (op.op === 'destroy') internPlaced(copies[0]!, op.command);
  const loose = creatures.filter(row => !copies.some(copy => (Array.isArray(copy.encounter_creatures_arr) ? copy.encounter_creatures_arr : []).includes(row)));
  const creatureIds = creatures.map(row => upper(asRow(row.encounter_creatures)?.encounter_creature_id));
  // Additional facets hold the same touched creature references; no entity is copied.
  const targets = copies.concat(loose.map(row => ({ encounter_creatures_arr: [row] })));
  const draw = applyChainOpsWithCorrection(targets, frame.ops, definition, report => {
    // republish the CURRENT rows (a captured row may have been replaced by a push since)
    for (const id of creatureIds) { const row = actorRowFor(encounterId, id) as Row | null; if (row) handleCasterUpdate({ type: 'caster_update', caster_arr: row as never, local: true }); }
    const now = encounterCopies(encounterId)[0];
    if (now) rerender(encounterId, now, 'inventory_destroy_reverted');
    if (report.conflicts) console.warn('[destruction-chain] undo kept newer changes', report.conflictPaths);
    window.dispatchEvent(new CustomEvent('ft:inventory-destroy-chain-undone', { detail: { operationId: upper(frame.operation_id), ...report } }));
  });
  for (const row of creatures) handleCasterUpdate({ type: 'caster_update', caster_arr: row as never, local: true });
  rerender(encounterId, copies[0]!, 'inventory_destroy_local');
  return draw;
}
/** Relays draw peers immediately; terminal frames settle the draw and adopt saved inputs on every page. */
function handleDestructionChain(frame: Row): void {
  const operationId = upper(frame.operation_id), encounterId = upper(frame.encounter_id);
  if (frame.pushed === true) {
    if (own.has(operationId) || !noteSeen(operationId, 'provisional') || !Array.isArray(frame.ops)) return;
    applyChainLocally(encounterId, frame as unknown as DestructionChainFrame)?.accept();
    window.dispatchEvent(new CustomEvent('ft:inventory-destroy-chain-received', { detail: { operationId } }));
    return;
  }
  if (frame.correction === true || frame.committed === true)
    window.dispatchEvent(new CustomEvent('ft:inventory-destroy-chain-saved', { detail: { operationId, saved: frame.committed === true, reason: frame.reason ?? null } }));
  if (frame.correction === true) {
    noteSeen(operationId, 'correction');
    // R-121: initiator and peers alike — the relayed / own draw reverts three-way (the saved encounter follows by push)
    if (chainDraws.correction(operationId, frame) === 'reverted' && own.has(operationId))
      warn(`Turn to rubble could not be saved (${String(frame.why ?? frame.reason ?? 'error')}). The encounter was restored.`);
    refreshSavedInventory(frame.base_refresh as BasePatchFrame | undefined);
    return;
  }
  if (frame.committed === true) {
    chainDraws.accept(operationId);
    if (noteSeen(operationId, 'committed')) refreshSavedInventory(frame.base_refresh as BasePatchFrame | undefined);
    return;
  }
  // a chain already committed / corrected is terminal: a late relay never draws it again (Codex S4 review #7)
  if (frame.provisional !== true || own.has(operationId) || !chainDraws.admit(operationId) || !noteSeen(operationId, 'provisional') || !Array.isArray(frame.ops)) return;
  let draw: ChainDraw | null = null;
  try { draw = applyChainLocally(encounterId, frame as unknown as DestructionChainFrame); }
  catch (error) { console.warn('[destruction-chain] a relayed chain could not be drawn; the saved encounter follows', error); return; }
  if (draw) chainDraws.track(operationId, draw);
  window.dispatchEvent(new CustomEvent('ft:inventory-destroy-chain-received', { detail: { operationId } }));
}

/**
 * R1b-2 PREVIEW (dry run, nothing applied or sent): the destruction as it WOULD convert if this page could drain the object's
 * payload — the command with its `payload` snapshot, and the drain plan (surfaces → bursts, gates, the exact casting strings
 * janus() would receive). The smoke compares both with PHP's own job (fixture `payload-oracle`) before the legacy endpoint
 * runs. Missing definitions (the pile, a child, a payload ability) are fetched, as the real click does.
 */
export async function previewDestructionPayload(input: { campaignId: string; encounterId: string; inventoryId: string; casterId?: string }):
  Promise<{ scope: unknown; plan: PayloadPlan | null }> {
  const encounterId = upper(input.encounterId);
  const caster: PayloadCaster = input.casterId ? { kind: 'creature', id: upper(input.casterId) } : { kind: 'user', id: upper(window.FateTide?.user?.id) };
  const asked = new Set<string>();
  for (let pass = 0; pass < 8; pass++) {
    const encounter = actorEncounterFor(encounterId) as Row | null;
    const roster = Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : [];
    const source = roster.find(r => inventoryId(r) === upper(input.inventoryId)) ?? null;
    if (!encounter || !source) return { scope: { supported: false, refused: false, reason: 'destroy-source-unavailable' }, plan: null };
    const isGM = window.FateTide?.user?.isGM === true
      || (window as unknown as { ftActiveViewerIsFullGm?: () => boolean }).ftActiveViewerIsFullGm?.() === true;
    const read = readers(encounter, Number(asRow(source.position)?.z_pos ?? 0) || 0);
    const sourceGeometry = drawnSourceGeometry(encounter, source, read);
    const scope = resolveObjectDestruction({ campaignId: input.campaignId, encounterId, inventoryId: input.inventoryId, isGM,
      sourceFootprintFt: sourceGeometry ? sourceGeometry.footprintM / 0.3048 : null, sourceGeometry,
      branches: { ...GM_TOOL_BRANCHES, payload: true }, payloadCaster: caster }, encounter, read);
    let want: string[] = [];
    let plan: PayloadPlan | null = null;
    if (!scope.supported) {
      if (!scope.refused) want = scope.anchorItemId ? [scope.anchorItemId] : scope.definitionIds ?? [];
    } else if (scope.command.payload) {
      plan = planPayloadDrain(read, scope.command.payload);
      if ('reason' in plan) want = plan.definitionIds ?? [];
    }
    const fresh = want.map(upper).filter(id => !asked.has(id));
    if (fresh.length === 0) return JSON.parse(JSON.stringify({ scope, plan })) as { scope: unknown; plan: PayloadPlan | null };
    fresh.forEach(id => asked.add(id));
    const fetched = await Promise.all(fresh.map(fetchAnchor));
    if (fetched.some(def => !def)) return JSON.parse(JSON.stringify({ scope, plan })) as { scope: unknown; plan: PayloadPlan | null };
  }
  return { scope: { supported: false, refused: false, reason: 'preview-budget' }, plan: null };
}

const anchorFetches = new Map<string, Promise<unknown>>();
function fetchAnchor(itemId: string): Promise<unknown> {
  const key = upper(itemId);
  let pending = anchorFetches.get(key);
  if (!pending) {
    // a miss or a failed fetch never sticks: the next attempt fetches again (a rejection resolves to null — the caller's hand-back path)
    pending = fetchAndCacheObject(key).then(def => { if (!def) anchorFetches.delete(key); return def; }, () => { anchorFetches.delete(key); return null; });
    anchorFetches.set(key, pending);
  }
  return pending;
}

/**
 * Null = unconverted: the caller runs the legacy endpoint. When only definitions are missing (the first pile of a
 * composition on this page; a container child's template — R1b-3) they are fetched (options/api/object_inc.php) and
 * the conversion retried: the pile first, then the contents, so at most three passes.
 */
export async function tryCommitObjectDestruction(input: DestroyInput): Promise<ObjectDestroyOutcome> {
  // Keep going while each pass learns something new (a nested container's child is only discovered once its parent's
  // template is held); a bounded budget, and a pass that asks for nothing new, end it (Codex R1b pass 2).
  const asked = new Set<string>();
  for (let pass = 0; pass < 8; pass++) {
    const result = attemptObjectDestruction(input);
    if (result === null || typeof result === 'string') return result;
    if ('payload' in result) return destroyWithPayload(result.payload);
    const fresh = result.fetch.map(upper).filter(id => !asked.has(id));
    if (fresh.length === 0) return null;
    fresh.forEach(id => asked.add(id));
    const fetched = await Promise.all(fresh.map(async id => {
      if (!id.startsWith('CORPSE:')) return fetchAnchor(id);
      const ecId = id.slice('CORPSE:'.length);
      try { await window.fetchAndCacheCaster?.(ecId, { skipEncounterSeed: true }); } catch { return null; }
      // Retry once even if the fetch completed without an inventory; the asked set then hands back on a second miss.
      return true;
    }));
    if (fetched.some(def => !def)) return null;
  }
  return null;
}

/** The destruction payload ability ids a roster row carries (its own layer, else its template's). */
function rowPayloadIds(encounter: Row, row: Row): string[] {
  return ftLayerEntries({ ...row, catalog: catalogOf(encounter, row) }, 'destruction_effect_ability')
    .map(entry => upper(typeof entry === 'string' ? entry.split('|')[0] : asRow(asRow(entry)?.items)?.item_id ?? asRow(entry)?.item_id))
    .filter(id => /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(id));
}
/** Every payload ability any object on this encounter's floor carries — what a chain can reach. */
function rosterPayloadIds(encounter: Row): string[] {
  const ids = new Set<string>();
  for (const row of Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as unknown[] : []) {
    const r = asRow(row);
    if (r) for (const id of rowPayloadIds(encounter, r)) ids.add(id);
  }
  return [...ids];
}
/**
 * What a CAUSED destruction of each nearby object would ask for (its rubble anchor, a container's contents' templates) — resolved dry the way
 * the chain resolves it, and fetched now, so the chain never pauses for a fetch pass. The nearest 200 rows to the source (a chain spreads
 * outward from it). Never mutates anything.
 */
async function warmCausedDefinitions(encounter: Row, source: Row, sourceOnly = false): Promise<void> {
  const at = asRow(source.position) ?? {};
  const dist = (r: Row): number => { const p = asRow(r.position) ?? {}; return Math.hypot(Number(p.x_pos ?? 0) - Number(at.x_pos ?? 0), Number(p.y_pos ?? 0) - Number(at.y_pos ?? 0)); };
  const rows = sourceOnly ? [source] : (Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as unknown[] : []).map(asRow)
    .filter((r): r is Row => !!r && r !== source).sort((a, b) => dist(a) - dist(b)).slice(0, 200);
  const encounterId = upper(asRow(encounter.encounters)?.encounter_id ?? encounter.encounter_id);
  const stub = upper(window.FateTide?.user?.id);
  for (let pass = 0; pass < 3; pass++) {
    const want = new Set<string>();
    for (const row of rows) {
      const id = inventoryId(row);
      const scope = resolveObjectDestruction({ campaignId: '00000000-0000-4000-8000-000000000000', encounterId, inventoryId: id, isGM: true,
        branches: { door: true, spill: true, payload: true }, payloadCaster: { kind: 'user', id: stub },
        cause: { kind: 'payload', roll_id: '00000000-0000-4000-8000-000000000001', source_inventory_id: '00000000-0000-4000-8000-000000000002', ability_id: '00000000-0000-4000-8000-000000000003' } },
        encounter, readers(encounter, 0));
      if (!scope.supported && !scope.refused) for (const f of scope.anchorItemId ? [scope.anchorItemId] : scope.definitionIds ?? []) want.add(upper(f));
    }
    if (!want.size) return;
    await Promise.all([...want].map(fetchAnchor));
  }
}
/** A falling source needs its own rubble/spill definitions before the dry runs draw. */
export async function prefetchDamageDestruction(input: { encounterId: string; inventoryId: string }): Promise<void> {
  const encounter = actorEncounterFor(upper(input.encounterId)) as Row | null;
  const source = (Array.isArray(encounter?.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : [])
    .find(row => inventoryId(row) === upper(input.inventoryId));
  if (encounter && source) await warmCausedDefinitions(encounter, source, true);
}
/** Warm the pile's definition while the GM is still choosing (radial GM Tools, the card's first rubble click), so
 * Confirm converts without a round trip. Never mutates anything. */
export function prefetchObjectDestruction(input: { inventoryId: string; encounterId?: string }): void {
  const cache = window.FateTide?.encounterCache as unknown;
  const encounterIds = input.encounterId ? [upper(input.encounterId)] : cache instanceof Map ? [...cache.keys()].map(upper) : [];
  const campaignId = upper((window.FateTide as { campaign?: { id?: string } } | undefined)?.campaign?.id) || '00000000-0000-4000-8000-000000000000';
  for (const encounterId of encounterIds) {
    const result = attemptObjectDestruction({ campaignId, encounterId, inventoryId: input.inventoryId }, true);
    // R1b-2f: a payload object also warms what its chain's pre-draw reads (its payload definitions and what they name, the members'
    // full compiles) — Confirm then draws without a round trip.
    const encounter = actorEncounterFor(encounterId) as Row | null;
    const source = (Array.isArray(encounter?.encounter_inventories_arr) ? encounter!.encounter_inventories_arr as Row[] : []).find(r => inventoryId(r) === upper(input.inventoryId));
    if (encounter && source && rowPayloadIds(encounter, source).length) {
      void prefetchPayloadDefinitions(encounter, rosterPayloadIds(encounter)).then(() => warmCausedDefinitions(encounter, source)).catch(() => null);
      void certifyMembers(encounterId).catch(() => null);
    }
    if (result && typeof result === 'object' && 'fetch' in result) {
      for (const id of result.fetch) {
        if (id.startsWith('corpse:')) void window.fetchAndCacheCaster?.(id.slice('corpse:'.length), { skipEncounterSeed: true }).catch(() => null);
        else void fetchAnchor(id);
      }
      return;
    }
  }
}

/** A `janus_inventory_destroy` frame: the lazy save's relay (schema janus.destruction-chain.v1) — provisional / committed / correction. */
export type InventoryDestroyWireFrame = Row & { schema: string; operation_id: string; encounter_id: string };
/** Peers draw a relayed chain at once; a correction drops it (the saved encounter follows by push). */
export function handleInventoryDestroyTruth(command: InventoryDestroyWireFrame): void {
  if (command?.schema === DESTRUCTION_CHAIN_SCHEMA) handleDestructionChain(command);
}

Object.assign(window, { ftTryCommitObjectDestruction: tryCommitObjectDestruction, ftPrefetchObjectDestruction: prefetchObjectDestruction,
  ftPlanObjectDestruction: planObjectDestruction, ftPreviewDestructionPayload: previewDestructionPayload });
